/* eslint-disable max-params */
/* eslint-disable max-depth */
/* eslint-disable complexity */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { SSEResponse } from '@patsnap-utils/sse';
import { PsHttpService } from '@patsnap/tahiti-core';
import { PsHttpRequest, type PsHttpSSE, type PsHttpSSEOptions } from '../sse-ps-http';
import { SSE_ERROR_MESSAGE_FIRST_RESPONSE_TIMEOUT, SSE_FIRST_RESPONSE_TIMEOUT, SSE_ERROR_MESSAGE_PROGRESS_TIMEOUT, SSE_PROGRESS_TIMEOUT } from '../constants';
import { StreamStatus } from '../enums';

interface SSeFragment {
    status: StreamStatus;
    content: unknown;
}

function tryParseMessage<T extends SSeFragment>(row: string, callback: (err: unknown) => void): SSEResponse<T> | null {
    try {
        return JSON.parse(row);
    } catch (err) {
        callback(err);
        return null;
    }
}

export function parseSseMessage<T extends SSeFragment>(
    input: string,
    onMessage: (message: SSEResponse<T>) => void,
    onFinish: () => void,
    onParseError: (error: unknown) => void,
) {
    if (!input) {
        return;
    }

    const rows = input.split('\n\n')
        .map(row => row.trim().replace(/^data:/, ''))
        .filter(row => row.trim())
        .reverse(); // 反转数组以便从最后一条消息开始处理

    let isFinished = false;
    let hasFinishBeenCalled = false;

    function handleFinish() {
        if (!hasFinishBeenCalled) {
            hasFinishBeenCalled = true;
            onFinish();
        }
    }

    // 查找最后一条有效消息
    const lastValidMessage = rows.find(row => {
        const message = tryParseMessage<T>(row, onParseError);
        if (!message) {
            return false;
        }

        if (message?.data?.status === StreamStatus.FINISH) {
            isFinished = true;
            if (message?.data?.content) {
                onMessage(message);
                handleFinish();
                return true;
            }
            return false;
        }

        if (message?.data?.content) {
            onMessage(message);
            if (isFinished) {
                handleFinish();
            }
            return true;
        }

        return false;
    });

    // 如果找到了 FINISH 状态但没找到有效消息，仍然需要触发 finish
    if (isFinished && !lastValidMessage) {
        handleFinish();
    }
}

@injectable()
export class SSEPsHttpImpl<T extends { content: string, status: StreamStatus }> implements PsHttpSSE<T> {
    private _abortController: AbortController | undefined;

    constructor(
        @inject(PsHttpRequest)
        private readonly _psHttpService: PsHttpService,
    ) {}

    get options() {
        return {};
    }

    private _options: PsHttpSSEOptions = {
        url: '',
    };

    private _onOpen: (response: Response) => void = () => {};
    private _onMessage: (data: SSEResponse<T>) => void = () => {};
    private _onError: (err: any) => void = () => {};
    private _onParseError: (error: any) => void = () => {};
    private _onClose: () => void = () => {};
    private _onFinish: () => void = () => {};

    private _customHeaders: Record<string, string> = {};

    init(options: PsHttpSSEOptions): this {
        this._options = options;
        return this;
    }

    private _disposeFirstProgressTimer = () => {};
    private _disposeProgressIntervalTimer = () => {};

    /**
     * 定时抛出 abort 错误
     * @param message 错误消息
     * @param delay 延迟时间
     * @returns 清理函数
     */
    private _throwAbortError(message: string, delay: number): () => void {
        let it: any = setTimeout(() => {
            this._abortController?.abort(message);
        }, delay);

        return () => {
            clearTimeout(it);
            it = undefined;
        };
    }

    /**
     * 开启首次响应超时定时器
     */
    private _startFirstResponseTimeoutTimer() {
        this._disposeFirstProgressTimer = this._throwAbortError(SSE_ERROR_MESSAGE_FIRST_RESPONSE_TIMEOUT, SSE_FIRST_RESPONSE_TIMEOUT);
    }

    /**
     * 开启进度超时定时器
     */
    private _startProgressTimeoutTimer() {
        this._disposeProgressIntervalTimer = this._throwAbortError(SSE_ERROR_MESSAGE_PROGRESS_TIMEOUT, SSE_PROGRESS_TIMEOUT);
    }

    /**
     * 移除已有的进度超时定时器，并开启新的进度超时定时器
     */
    private _resetProgressTimeoutTimer() {
        this._disposeProgressIntervalTimer();
        this._startProgressTimeoutTimer();
    }

    async start(): Promise<void> {
        this._abortController = new AbortController();
        this._onOpen(new Response());

        this._startFirstResponseTimeoutTimer();

        await this._psHttpService.request({
            // @ts-ignore
            baseURL: this._options.baseURL,
            method: this._options.method as any || ('POST' ),
            url: this._options.url,
            data: this._options.data,
            headers: this._customHeaders,
            onDownloadProgress: (progressEvent) => {
                this._handleOnDownloadProgress(progressEvent);
            },
            signal: this._abortController.signal,
            timeout: Number.MAX_SAFE_INTEGER,
        })
            .catch((err) => {
                const abortSignal = this._abortController?.signal;
                this._onError(abortSignal?.aborted ? new Error(abortSignal.reason) : err);
                throw err;
            })
            .finally(() => {
                this._disposeFirstProgressTimer();
                this._disposeProgressIntervalTimer();
                this._onClose();
            });
    }

    private _handleOnDownloadProgress(ev: { target: { response?: string } }) {
        this._disposeFirstProgressTimer();
        this._resetProgressTimeoutTimer();
        this._handleProgress(ev);
    }

    private _handleProgress(ev: { target: { response?: string } }) {
        const responseText = ev.target?.response || '';
        parseSseMessage(
            responseText,
            (message) => this._onMessage(message as SSEResponse<T>),
            () => this._onFinish(),
            (error) => this._onParseError(error),
        );
    }

    onOpen(callback: (response: Response) => void): this {
        this._onOpen = callback;
        return this;
    }

    onMessage(callback: (data: SSEResponse<T>) => void): this {
        this._onMessage = callback;
        return this;
    }

    onFinish(callback: () => void): this {
        this._onFinish = callback;
        return this;
    }

    onClose(callback: () => void): this {
        this._onClose = callback;
        return this;
    }

    onError(callback: (err: any) => void): this {
        this._onError = callback;
        return this;
    }

    onParseError(callback: (error: any) => void): this {
        this._onParseError = callback;
        return this;
    }

    close() {
        this._disposeFirstProgressTimer();
        this._disposeProgressIntervalTimer();
        this._abortController?.abort();
        this._onClose();
    }

    setFetchHeaders(customHeaders?: Record<string, string>): this {
        this._customHeaders = customHeaders || {};
        return this;
    }
}
